import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';
import { useCreatePurchaseOrder } from '@api/hooks/usePendingDocuments';
import { ExpressionNumberInput } from '@components/common/ExpressionNumberInput';
import { coerceNumericExpressionValue } from '@/lib/numericExpressions';
import type { CreatePurchaseOrderRequest } from '@schemas/api';

interface PurchaseOrderCreationFormProps {
  initialPoNumber: string;
  vendorId?: string;
  onSuccess?: () => void;
  onClose: () => void;
  missingReferenceId?: string;  // Public reference of the missing reference
}

const poSchema = z.object({
  po_number: z.string().min(1, 'PO number is required'),
  vendor_id: z.string().min(1, 'Vendor must be selected'),
  currency: z.string().length(3, 'Currency must be 3 letters'),
  total: z.coerce.number().positive('Total must be positive'),
  tax_total: z.coerce.number().nonnegative().optional(),
});

type POFormData = z.infer<typeof poSchema>;

export const PurchaseOrderCreationForm: React.FC<PurchaseOrderCreationFormProps> = ({
  initialPoNumber,
  vendorId,
  onSuccess,
  onClose,
  missingReferenceId,
}) => {
  const createPO = useCreatePurchaseOrder();
  const [vendorIdValid, setVendorIdValid] = useState(false);

  useEffect(() => {
    setVendorIdValid(Boolean(vendorId));
  }, [vendorId]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<POFormData>({
    resolver: zodResolver(poSchema),
    defaultValues: {
      po_number: initialPoNumber,
      vendor_id: vendorId || '',
      currency: 'USD',
      total: 0,
      tax_total: 0,
    },
  });

  const total = coerceNumericExpressionValue(watch('total')) ?? 0;
  const taxTotal = coerceNumericExpressionValue(watch('tax_total')) ?? 0;
  const grandTotal = total + taxTotal;

  const onSubmit = async (data: POFormData) => {
    if (!vendorIdValid) {
      alert('Please select a vendor first');
      return;
    }

    const request: CreatePurchaseOrderRequest = {
      ...data,
      vendor_id: vendorId || '',
      missing_reference_id: missingReferenceId,
    };
    await createPO.mutateAsync(request);
    if (onSuccess) {
      setTimeout(onSuccess, 500);
    }
  };

  const isLoading = createPO.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">Create Purchase Order</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!vendorIdValid && (
          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 mb-4">
            <p className="text-sm text-yellow-800">
              Please create the missing vendor first before creating a PO.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* PO Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PO Number *
            </label>
            <input
              type="text"
              {...register('po_number')}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="e.g., PO-2024-001"
            />
            {errors.po_number && (
              <p className="mt-1 text-sm text-red-600">{errors.po_number.message}</p>
            )}
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Currency *
            </label>
            <input
              type="text"
              {...register('currency')}
              disabled={isLoading}
              maxLength={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500 uppercase"
              placeholder="USD"
            />
            {errors.currency && (
              <p className="mt-1 text-sm text-red-600">{errors.currency.message}</p>
            )}
          </div>

          {/* Subtotal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subtotal *
            </label>
            <Controller
              name="total"
              control={control}
              render={({ field }) => (
                <ExpressionNumberInput
                  step="0.01"
                  value={field.value}
                  onValueChange={(value) => field.onChange(value === null ? '' : value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="0.00"
                />
              )}
            />
            {errors.total && (
              <p className="mt-1 text-sm text-red-600">{errors.total.message}</p>
            )}
          </div>

          {/* Tax Total */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax (Optional)
            </label>
            <Controller
              name="tax_total"
              control={control}
              render={({ field }) => (
                <ExpressionNumberInput
                  step="0.01"
                  value={field.value}
                  onValueChange={(value) => field.onChange(value === null ? '' : value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="0.00"
                />
              )}
            />
            {errors.tax_total && (
              <p className="mt-1 text-sm text-red-600">{errors.tax_total.message}</p>
            )}
          </div>

          {/* Grand Total Display */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-600">Grand Total</p>
            <p className="text-2xl font-bold text-gray-900">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: watch('currency') || 'USD',
              }).format(grandTotal)}
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !vendorIdValid}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create PO'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
